/*
 * Copyright (c) Nexign, JSC, 2024.
 */
/**
 *
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.28.0
 */

import { IPageHistoryInquiryTranslation } from '@/main/modules/internationalization/types';
import {
    TTableColumnConfig,
    TableCellDate,
    TableCellLinkTooltipText,
    TableCellStatus,
    TableCellText,
    TableFilterMultiSelect,
    TableFilterRangeDatePicker,
    TableHeader,
    useTableCellAccount,
} from '@/main/modules/table';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { ITableOtherInquiry } from '../types';
import { getInquiryStatusIcon } from '../utils/getInquiryStatusIcon';

export const TableOtherInquiryColumns = ({
    table,
}: IPageHistoryInquiryTranslation): TTableColumnConfig<ITableOtherInquiry> => {
    const useAccountCell = ({ account }: ITableOtherInquiry) =>
        useTableCellAccount(account.id.toString(), account.number);

    return {
        /* Обращение № */
        id: {
            accessor: 'id',
            Header: (
                <TableHeader label={table.id.header} name="id">
                    <TableFilterMultiSelect name="id" />
                </TableHeader>
            ),
            ...TableCellText<ITableOtherInquiry>(({ id }) => id),
        },
        /* Лицевой счет */
        account: {
            accessor: 'account',
            Header: (
                <TableHeader label={table.account.header} name="account">
                    <TableFilterMultiSelect name="account" />
                </TableHeader>
            ),
            ...TableCellLinkTooltipText<ITableOtherInquiry>(useAccountCell),
        },
        /* Номер */
        subscriberNumber: {
            accessor: 'subscriberNumber',
            Header: (
                <TableHeader label={table.subscriberNumber.header} name="subscriberNumber">
                    <TableFilterMultiSelect name="subscriberNumber" />
                </TableHeader>
            ),
            ...TableCellText<ITableOtherInquiry>(({ subscriberNumber }) => subscriberNumber),
        },
        /* Дата */
        createDate: {
            accessor: 'createDate',
            Header: (
                <TableHeader label={table.createDate.header} name="createDate">
                    <TableFilterRangeDatePicker name="createDate" />
                </TableHeader>
            ),
            ...TableCellDate<ITableOtherInquiry>(({ createDate }) => ({
                date: createDate,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        },
        /* Полное время окончания (отключено BBPORTAL-14497)
        closeDate: {
            accessor: 'closeDate',
            Header: (
                <TableHeader label={table.closeDate.header} name="closeDate">
                    <TableFilterRangeDatePicker name="closeDate" />
                </TableHeader>
            ),
            ...TableCellDate<ITableOtherInquiry>(({ closeDate }) => ({
                date: closeDate,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        }, */
        /* Обновлено */
        updateDate: {
            accessor: 'updateDate',
            Header: (
                <TableHeader label={table.updateDate.header} name="updateDate">
                    <TableFilterRangeDatePicker name="updateDate" />
                </TableHeader>
            ),
            ...TableCellDate<ITableOtherInquiry>(({ updateDate }) => ({
                date: updateDate,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        },
        /* Тип обращения */
        topic: {
            accessor: 'topic',
            Header: <TableHeader disableSort label={table.topic.header} name="topic" />,
            ...TableCellText<ITableOtherInquiry>(({ topic }) => topic),
        },
        /* Статус */
        status: {
            accessor: 'status',
            Header: (
                <TableHeader label={table.status.header} name="status">
                    <TableFilterMultiSelect name="status" />
                </TableHeader>
            ),
            ...TableCellStatus<ITableOtherInquiry>(({ status, statusId }) => ({
                icon: getInquiryStatusIcon(statusId),
                status,
            })),
        },
        /* Отчет по обработке */
        processingReport: {
            accessor: 'processingReport',
            Header: <TableHeader disableSort label={table.processingReport.header} name="processingReport" />,
            ...TableCellText<ITableOtherInquiry>(({ processingReport }) => processingReport),
        },
    };
};
